import { useEffect, useMemo, useState } from 'react';
import { OPENED_STATES } from '../data/states.js';
import { recordScore } from '../utils/storage.js';
import { track } from '../utils/tracking.js';
import { MODES, SCOPES } from '../utils/quiz.js';
import ReplayGate from './ReplayGate.jsx';
import EmailCapture from './EmailCapture.jsx';
import QuizMap from './QuizMap.jsx';
import UnicornUnlock from './UnicornUnlock.jsx';

function scoreMessage(pct) {
  if (pct === 1)    return "A perfect round. Absolutely cooking.";
  if (pct >= 0.9)   return "Geographic gold star. Take a bow.";
  if (pct >= 0.7)   return "Solid. Few stumbled, most stuck.";
  if (pct >= 0.5)   return "Halfway there. Run it back.";
  if (pct >= 0.3)   return "Some hits, some misses. Keep going.";
  return "Brutal round. The states are tough.";
}

// Alphabetical, full-name list of OpenEd states for the results roll call.
const OPENED_STATES_ALPHA = [...OPENED_STATES].sort((a, b) => a.name.localeCompare(b.name));

export default function ResultsScreen({ result, onPlayAgain, onHome }) {
  const { score, total, answers, mode, scope } = result;
  const pct = score / total;

  const openedAppeared = useMemo(
    () => answers.filter(a => a.isOpenEd),
    [answers]
  );
  const openedCorrect = openedAppeared.filter(a => a.isCorrect);

  // Animated score tick-up. Ticks from 0 to final score over ~1.2s with ease-out pacing.
  const [displayScore, setDisplayScore] = useState(0);
  const [displayPct, setDisplayPct] = useState(0);
  useEffect(() => {
    if (score === 0) { setDisplayScore(0); setDisplayPct(0); return; }
    const start = performance.now();
    const durationMs = 1200;
    let frame;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setDisplayScore(Math.round(eased * score));
      setDisplayPct(Math.round(eased * Math.round(pct * 100)));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [score, pct]);

  useEffect(() => {
    recordScore(mode, scope, score, total);
    track('quiz_finished', {
      mode,
      scope,
      score,
      total,
      pct: Math.round(pct * 100) / 100,
      openedAppeared: openedAppeared.length,
      openedCorrect: openedCorrect.length,
    });
  }, [mode, scope, score, total, pct, openedAppeared.length, openedCorrect.length]);

  return (
    <div className="fade-in">
      <section className="results-hero">
        <p className="eyebrow fade-in delay-1">Round complete</p>
        <div className="results-score fade-in delay-2">
          {displayScore}<span className="denom"> / {total}</span>
          <span className="pct">{displayPct}%</span>
        </div>
        <p className="results-message fade-in delay-3">{scoreMessage(pct)}</p>
      </section>

      {pct === 1 && <UnicornUnlock score={score} total={total} />}

      <ReplayGate
        onPlayAgain={onPlayAgain}
        result={result}
      />

      <div className="results-map fade-in delay-3">
        <QuizMap answers={answers} />
      </div>

      <div className="results-detail fade-in delay-3">
        <h3>Scoreboard</h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
          <div className="opened-tally">
            <span className="num">{openedCorrect.length}</span>
            <span className="label">
              of {openedAppeared.length} OpenEd states correct
            </span>
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem', color: 'var(--ink-faint)' }}>
            {MODES[mode].name} · {SCOPES[scope].name}
          </div>
        </div>

        <div className="state-chips">
          {answers.map((a, i) => (
            <span
              key={`${a.stateAbbr}-${i}`}
              className={`state-chip ${a.isOpenEd && a.isCorrect ? 'is-opened' : ''} ${!a.isCorrect ? 'is-missed' : ''}`}
              title={`${a.stateName}${a.isOpenEd ? ' (OpenEd)' : ''} — ${a.isCorrect ? 'correct' : 'missed'}`}
            >
              {a.stateAbbr}
            </span>
          ))}
        </div>
      </div>

      <section className="opened-roll-call fade-in delay-4">
        <h3>OpenEd operates in these 15 states</h3>
        <ul className="state-roll-list">
          {OPENED_STATES_ALPHA.map(s => (
            <li key={s.abbr}>{s.name}</li>
          ))}
        </ul>
        <p className="roll-call-footnote">
          Resources and support for homeschool families. <a href="https://opened.co" target="_blank" rel="noopener noreferrer">Learn more about OpenEd →</a>
        </p>
      </section>

      <EmailCapture quizStats={{ score, total, openedCorrect: openedCorrect.length }} />
    </div>
  );
}
